// components/ContactForm.js
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './ContactForm.css';

const ContactForm = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    interest: '',
    message: ''
  });
  useEffect(() => {
    const interestParam = searchParams.get('interest');
    if (interestParam) {
      setFormData(prev => ({
        ...prev,
        interest: interestParam
      }));
    }
  }, [searchParams]);
  useEffect(() => {
    const datasetParam = searchParams.get('dataset');
    if (datasetParam) {
      setFormData(prev => ({
        ...prev,
        message: `I'd like to know more about the ${datasetParam} dataset...`
      }));
    }
  }, [searchParams]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });
    try {
      const response = await fetch('https://formspree.io/f/mzzgqvel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus({
          type: 'success',
          message: 'Thank you for your message. We\'ll get back to you soon!'
        });
        setFormData({
          name: '',
          email: '',
          company: '',
          interest: '',
          message: ''
        });
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="contact-section" id="contact-form">
      <div className="contact-container">
        <div className="contact-header">
          <h2 className="contact-title">
            Let's Build <span className="title-accent">Something Amazing</span>
          </h2>
          <p className="contact-subtitle">
            Whether you need custom datasets, consulting, or want to explore our existing solutions,
            we're here to help accelerate your ML journey.
          </p>
        </div>

        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name" className="form-label">Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@company.com"
                className="form-input"
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="company" className="form-label">Company</label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="Your Company"
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="interest" className="form-label">I'm interested in</label>
              <div className="select-wrapper">
                <select
                  id="interest"
                  name="interest"
                  value={formData.interest}
                  onChange={handleChange}
                  className="form-select"
                  required
                >
                  <option value="" disabled>Select an option</option>
                  <option value="enterprise-datasets">Enterprise Datasets</option>
                  <option value="custom-datasets">Custom Dataset Creation</option>
                  <option value="consulting">ML Consulting</option>
                  <option value="partnership">Partnership Opportunities</option>
                  <option value="other">Other</option>
                </select>
                <svg className="select-icon" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="message" className="form-label">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your project and how we can help..."
              className="form-textarea"
              rows="5"
              required
            />
          </div>

          <button
            type="submit"
            className="form-submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="submit-spinner"></span>
                <span>Sending...</span>
              </>
            ) : (
              <>
                <span>Submit</span>
                {/* <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M2.5 10H17.5M17.5 10L12.5 5M17.5 10L12.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg> */}
              </>
            )}
          </button>

          {status.message && (
            <div className={`status-message ${status.type}`}>
              {status.type === 'success' ? (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M6 10L9 13L14 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M10 6V10M10 14H10.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
              <span>{status.message}</span>
            </div>
          )}
        </form>
      </div>
    </section>
  );
};

export default ContactForm;
